import { supabase } from './supabase';

/**
 * Parse a maintenance invoice/receipt/work-order (PDF or image) using Google
 * Gemini. Gemini reads the file directly — no separate OCR step — which is more
 * reliable than the old Document AI -> ChatGPT pipeline for messy shop invoices.
 *
 * The API key + model are read from system_settings (configured in
 * Settings -> Integrations -> Google Gemini AI). The call is made directly from
 * the browser, matching how the rest of the app uses provider keys
 * (see services/aiLoadAssignment.ts).
 *
 * Returns the raw extracted object (snake_case fields) so callers can map it the
 * same way the previous edge-function response was mapped.
 */

const EXTRACTION_PROMPT = `You are an intelligent maintenance invoice parser for a trucking/transportation company. Read the attached document (it may be a PDF or a photo of an invoice/receipt/work order) and extract ALL available information. Return ONLY a valid JSON object with this exact structure:
{
  "truck_number": "string or null - the truck/tractor unit number",
  "trailer_number": "string or null - the trailer unit number",
  "maintenance_type": "string - one of: Preventive Maintenance, Oil Change, Tire Service, Brake Service, Engine Repair, Transmission Repair, Electrical Repair, Body Work, DOT Inspection, Annual Inspection, Other",
  "description": "string - detailed description of work performed",
  "cost": number or null - total invoice amount including tax,
  "labor_cost": number or null - labor charges only,
  "parts_cost": number or null - parts/materials charges only,
  "tax": number or null - sales tax amount,
  "service_provider": "string or null - shop/vendor name",
  "provider_email": "string or null - vendor email address",
  "provider_phone": "string or null - vendor phone in format (XXX) XXX-XXXX",
  "street": "string or null - vendor street address",
  "city": "string or null - vendor city",
  "state": "string or null - vendor state 2 letter code",
  "zip_code": "string or null - vendor zip code",
  "service_date": "YYYY-MM-DD or null",
  "odometer": number or null - mileage/odometer reading,
  "vin": "string or null - 17 character VIN",
  "gl_account": "string - one of: 5100-Maintenance, 5110-Repairs, 5120-Tires, 5130-Oil & Lubricants, 5140-Parts, 5150-Labor, 5160-Inspections, 5900-Other Maintenance",
  "line_items": [
    {
      "item_type": "labor or parts",
      "description": "string",
      "quantity": number,
      "rate": number or null,
      "amount": number
    }
  ]
}

EXTRACTION RULES:
- TRUCK NUMBER: Look for "Fleet ID", "Tractor #", "Unit #", "Vehicle ID". For Fleet IDs like "NE546861", extract the numeric part.
- TRAILER NUMBER: Look for "Trailer #", "TRL#", trailer unit numbers
- SERVICE DATE: Convert any date format to YYYY-MM-DD. Look for "Date", "Invoice Date", "Service Date", "Work Order Date", "Close Date"
- ODOMETER: Look for "Mileage", "Odometer", "Hubometer", "Miles". Extract the number only
- VIN: Look for 17-character alphanumeric Vehicle Identification Number
- COSTS: Extract labor, parts, and total separately. Total should include tax. Look for "Balance Due", "Total", "Amount Due", "Grand Total"
- LINE ITEMS: Extract individual labor and parts items with quantities and amounts
- SERVICE PROVIDER: Extract shop/vendor name, usually at the top of the invoice
- ADDRESS: Extract full vendor address including street, city, state, zip
- GL ACCOUNT: Oil Change/PM -> 5130-Oil & Lubricants; Tire work -> 5120-Tires; DOT/Annual Inspection -> 5160-Inspections; General repairs -> 5110-Repairs; Preventive Maintenance -> 5100-Maintenance; Other -> 5900-Other Maintenance
- If information is not found, use null. Do NOT invent or guess values that are not present in the document.
- Return ONLY the JSON object, no markdown, no explanation.`;

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Strip the "data:<mime>;base64," prefix.
      resolve(result.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Map retired Gemini model names to a current one, so a stale saved value
// (e.g. gemini-2.0-flash, which Google has retired) doesn't 404. Falls back to a
// current default when unset.
export function normalizeGeminiModel(saved?: string | null): string {
  const RETIRED: Record<string, string> = {
    'gemini-2.0-flash': 'gemini-2.5-flash',
    'gemini-2.0-flash-exp': 'gemini-2.5-flash',
    'gemini-1.5-flash': 'gemini-2.5-flash',
    'gemini-1.5-pro': 'gemini-2.5-pro',
    'gemini-pro': 'gemini-2.5-pro',
  };
  const m = (saved || '').trim();
  return RETIRED[m] || m || 'gemini-2.5-flash';
}

export async function parseMaintenanceWithGemini(file: File): Promise<Record<string, any>> {
  // Use the server-side edge function (keeps the API key off the browser). Fall
  // back to a direct browser call ONLY when the function genuinely isn't deployed
  // — never on a real Gemini error (e.g. 429 quota), so we don't call Gemini
  // twice and burn double the quota.
  const base64 = await fileToBase64(file);
  const mimeType = file.type || (file.name.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'application/octet-stream');

  // NOTE: the deployed function slug is MISSPELLED ('maintanance'). This MUST
  // match the slug as deployed in Supabase — do NOT "correct" the spelling here
  // or every call 404s and silently falls back to a browser Gemini call, which
  // breaks for non-admin users (RLS blocks them reading the key client-side).
  const { data, error } = await supabase.functions.invoke('process-maintanance-pdf', {
    body: { fileBase64: base64, fileName: file.name, mimeType },
  });

  if (!error && data?.success && data.data) {
    return data.data;
  }

  // Pull the function's real error message + HTTP status if it ran and failed.
  let serverError = '';
  let status = 0;
  if (error) {
    const ctx: any = (error as any).context;
    status = ctx?.status ?? 0;
    try {
      const body = await ctx?.json?.();
      serverError = body?.error || '';
    } catch {
      /* body wasn't JSON */
    }
    if (!serverError) serverError = error.message || '';
  } else if (data && data.success === false) {
    serverError = data.error || '';
  }

  // Only fall back when the function isn't there (404 / unreachable).
  const notDeployed =
    status === 404 ||
    /not found|does not exist|failed to send a request to the edge function/i.test(serverError);
  if (notDeployed) {
    console.warn('process-maintanance-pdf not deployed; using direct Gemini call.');
    return parseDirectWithGemini(file);
  }

  console.error('process-maintanance-pdf failed:', serverError);
  throw new Error(serverError || 'Document parsing failed. Please try again.');
}

async function parseDirectWithGemini(file: File): Promise<Record<string, any>> {
  // Load the Gemini configuration saved in Settings -> Integrations.
  const { data: settings, error: settingsError } = await supabase
    .from('system_settings')
    .select('setting_key, setting_value')
    .in('setting_key', ['gemini_api_key', 'gemini_enabled', 'gemini_model']);

  if (settingsError) {
    throw new Error('Could not load Gemini settings. Please try again.');
  }

  const map = new Map((settings || []).map(s => [s.setting_key, s.setting_value]));
  const apiKey = map.get('gemini_api_key') || '';
  const enabled = map.get('gemini_enabled') === 'true';
  const model = normalizeGeminiModel(map.get('gemini_model'));

  if (!enabled || !apiKey) {
    throw new Error('Google Gemini is not configured. Go to Settings → Integrations → Google Gemini AI, add your API key, and enable it.');
  }

  const base64 = await fileToBase64(file);
  const mimeType = file.type || (file.name.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'application/octet-stream');

  const response = await fetch(
    `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${apiKey}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contents: [
          {
            parts: [
              { inline_data: { mime_type: mimeType, data: base64 } },
              { text: EXTRACTION_PROMPT },
            ],
          },
        ],
        generationConfig: {
          temperature: 0.1,
          responseMimeType: 'application/json',
        },
      }),
    }
  );

  if (!response.ok) {
    const errText = await response.text().catch(() => '');
    console.error('Gemini API error:', response.status, errText);
    // Gemini returns { error: { message, status } } — surface the real reason.
    let detail = '';
    try { detail = JSON.parse(errText)?.error?.message || ''; } catch { /* not JSON */ }
    if (response.status === 429) {
      throw new Error(
        `Gemini quota/rate limit (429): ${detail || 'your API key is out of quota.'} ` +
        `Enable billing on the key's Google project (Gemini Flash is ~fractions of a cent per invoice), or wait if this is a per-minute limit.`
      );
    }
    if (response.status === 400 || response.status === 403) {
      throw new Error(`Gemini rejected the request (${response.status}): ${detail || 'check the API key is valid and the Generative Language API is enabled.'}`);
    }
    throw new Error(`Gemini request failed (${response.status}): ${detail || 'please try again.'}`);
  }

  const result = await response.json();
  const text: string = result?.candidates?.[0]?.content?.parts?.[0]?.text || '';

  if (!text) {
    throw new Error('Gemini returned no data for this document. Please fill the form manually.');
  }

  try {
    // responseMimeType: 'application/json' should give clean JSON, but strip
    // any stray markdown fences just in case.
    const jsonMatch = text.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
    const jsonString = (jsonMatch ? jsonMatch[1] : text).trim();
    return JSON.parse(jsonString);
  } catch (parseError) {
    console.error('Failed to parse Gemini JSON response:', parseError, text);
    throw new Error('Could not read the data Gemini returned. Please fill the form manually.');
  }
}
